// ─────────────────────────────────────────────────────────────────────────────
// Invoices.js
// Contractor invoices list page.
//
// FEATURES:
//   - Create invoice from scratch (standalone)
//   - Convert an accepted/paid quote to invoice (1-click from quote)
//   - List all invoices with search, status filter, sort
//   - View invoice detail at /invoices/:id
//   - Status flow: Draft → Sent → Paid / Overdue / Cancelled
//
// DATABASE TABLES NEEDED:
//   - invoices (id, company_id, customer_id, invoice_number_numeric,
//               quote_id (nullable), status, reference, issue_date,
//               due_date, notes, payment_terms, terms_conditions,
//               vat_enabled, vat_rate, subtotal, vat_amount, total,
//               created_at)
//   - invoice_items (id, invoice_id, description, quantity, unit_price, total)
//
// Run this SQL to create the tables:
//   See bottom of this file for SQL
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Sidebar from '../components/Sidebar'
import { useNavigate } from 'react-router-dom'
import TrialBanner from '../components/TrialBanner'

export default function Invoices({ session }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([])
  const [customers, setCustomers] = useState([])
  const [quotes, setQuotes] = useState([]) // accepted quotes available to convert
  const [company, setCompany] = useState(null)
  const [companyId, setCompanyId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [convertingQuote, setConvertingQuote] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('All')
  const [sortBy, setSortBy] = useState('newest')
  const navigate = useNavigate()

  const defaultItems = [{ description: 'Labour', quantity: 1, unit_price: 0, total: 0 }]

  const [form, setForm] = useState({
    customer_id: '',
    quote_id: '',
    reference: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: '',
    payment_terms: '',
    terms_conditions: '',
    vat_enabled: true,
    vat_rate: 20,
    items: defaultItems,
  })

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', session.user.id)
      .single()

    if (profile?.company_id) {
      setCompanyId(profile.company_id)
      const [{ data: inv }, { data: c }, { data: co }, { data: q }] = await Promise.all([
        supabase.from('invoices')
          .select(`*, customers(customer_name, customer_type, email, phone)`)
          .eq('company_id', profile.company_id)
          .order('created_at', { ascending: false }),
        supabase.from('customers').select('*').eq('company_id', profile.company_id).order('customer_name'),
        supabase.from('companies').select('*').eq('id', profile.company_id).single(),
        // Only fetch accepted/paid quotes that haven't been converted yet
        supabase.from('quotes')
          .select(`*, customers(customer_name)`)
          .eq('company_id', profile.company_id)
          .in('status', ['Accepted', 'Paid'])
          .order('created_at', { ascending: false }),
      ])
      setInvoices(inv || [])
      setCustomers(c || [])
      setCompany(co)
      // Filter out quotes already converted to invoices
      const convertedQuoteIds = (inv || []).map(i => i.quote_id).filter(Boolean)
      setQuotes((q || []).filter(quote => !convertedQuoteIds.includes(quote.id)))
      if (co) {
        setForm(f => ({
          ...f,
          payment_terms: co.payment_terms || 'Payment due within 30 days',
          terms_conditions: co.terms_conditions || '',
        }))
      }
    }
    setLoading(false)
  }

  // ── Form helpers ───────────────────────────────────────────────────────────
  const resetForm = () => {
    setForm({
      customer_id: '', quote_id: '', reference: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: '',
      payment_terms: company?.payment_terms || 'Payment due within 30 days',
      terms_conditions: company?.terms_conditions || '',
      vat_enabled: true, vat_rate: 20, items: defaultItems,
    })
    setError(null)
  }

  const updateItem = (index, field, value) => {
    const items = [...form.items]
    items[index] = { ...items[index], [field]: value }
    if (field === 'quantity' || field === 'unit_price') {
      items[index].total = parseFloat(items[index].quantity || 0) * parseFloat(items[index].unit_price || 0)
    }
    setForm({ ...form, items })
  }

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0, total: 0 }] })
  const removeItem = (index) => {
    if (form.items.length === 1) return
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) })
  }

  const subtotal = form.items.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
  const vatAmount = form.vat_enabled ? subtotal * (parseFloat(form.vat_rate) / 100) : 0
  const grandTotal = subtotal + vatAmount

  // ── Convert quote to invoice ───────────────────────────────────────────────
  // Pre-fills the form with the selected quote's data
  const handleSelectQuoteToConvert = async (quote) => {
    const { data: quoteItems } = await supabase
      .from('quote_items')
      .select('*')
      .eq('quote_id', quote.id)

    setForm({
      customer_id: quote.customer_id,
      quote_id: quote.id,
      reference: quote.reference || '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: quote.notes || '',
      payment_terms: quote.payment_terms || company?.payment_terms || '',
      terms_conditions: quote.terms_conditions || company?.terms_conditions || '',
      vat_enabled: quote.vat_enabled ?? true,
      vat_rate: quote.vat_rate || 20,
      items: (quoteItems || []).map(i => ({
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price,
        total: i.total,
      })),
    })
    setConvertingQuote(quote)
    setShowConvertModal(false)
    setShowAddModal(true)
  }

  // ── Create invoice ─────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.customer_id) { setError('Please select a customer.'); return }
    if (form.items.some(i => !i.description.trim())) { setError('All line items need a description.'); return }
    setSaving(true)
    setError(null)

    // Get next invoice number — stored on companies table
    const { data: co } = await supabase
      .from('companies')
      .select('next_invoice_number')
      .eq('id', companyId)
      .single()

    // Default to 1 if column doesn't exist yet
    const invoiceNumber = co?.next_invoice_number || 1

    const { data: newInvoice, error: insertError } = await supabase
      .from('invoices')
      .insert([{
        company_id: companyId,
        customer_id: form.customer_id,
        quote_id: form.quote_id || null,
        invoice_number_numeric: invoiceNumber,
        status: 'Draft',
        reference: form.reference.trim(),
        issue_date: form.issue_date || null,
        due_date: form.due_date || null,
        notes: form.notes.trim(),
        payment_terms: form.payment_terms.trim(),
        terms_conditions: form.terms_conditions.trim(),
        vat_enabled: form.vat_enabled,
        vat_rate: parseFloat(form.vat_rate),
        subtotal, vat_amount: vatAmount, total: grandTotal,
      }])
      .select()
      .single()

    if (insertError) { setError(insertError.message); setSaving(false); return }

    await supabase.from('invoice_items').insert(
      form.items.map(item => ({
        invoice_id: newInvoice.id,
        description: item.description,
        quantity: parseFloat(item.quantity),
        unit_price: parseFloat(item.unit_price),
        total: parseFloat(item.total),
      }))
    )

    // Increment invoice number
    await supabase.from('companies')
      .update({ next_invoice_number: invoiceNumber + 1 })
      .eq('id', companyId)

    // If converted from quote, update quote status to reflect conversion
    if (form.quote_id) {
      await supabase.from('quotes')
        .update({ status: 'Paid' })
        .eq('id', form.quote_id)
    }

    setShowAddModal(false)
    setConvertingQuote(null)
    resetForm()
    fetchData()
    setSaving(false)
  }

  // ── Status change ──────────────────────────────────────────────────────────
  const handleStatusChange = async (invoiceId, newStatus) => {
    await supabase.from('invoices').update({ status: newStatus }).eq('id', invoiceId)
    fetchData()
  }

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return
    await supabase.from('invoices').delete().eq('id', invoiceId)
    fetchData()
  }

  // ── Filtering & sorting ────────────────────────────────────────────────────
  const filteredInvoices = invoices
    .filter(inv => {
      const matchesStatus = statusFilter === 'All' || inv.status === statusFilter
      const matchesSearch = search === '' ||
        inv.customers?.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        `INV-${String(inv.invoice_number_numeric).padStart(3, '0')}`.toLowerCase().includes(search.toLowerCase())
      return matchesStatus && matchesSearch
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at)
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at)
      if (sortBy === 'highest') return parseFloat(b.total || 0) - parseFloat(a.total || 0)
      if (sortBy === 'lowest') return parseFloat(a.total || 0) - parseFloat(b.total || 0)
      return 0
    })

  // ── Status styles ──────────────────────────────────────────────────────────
  const statusStyle = (status) => {
    const map = {
      Draft:     { background: '#F1F5F9', color: '#64748B' },
      Sent:      { background: '#EFF6FF', color: '#2563EB' },
      Paid:      { background: '#F0FDF4', color: '#15803D' },
      Overdue:   { background: '#FFF1F2', color: '#BE123C' },
      Cancelled: { background: '#F1F5F9', color: '#94A3B8' },
    }
    return {
      ...(map[status] || map.Draft),
      padding: '3px 10px', borderRadius: '999px',
      fontSize: '12px', fontWeight: '600',
      display: 'inline-block', whiteSpace: 'nowrap',
    }
  }

  // ── Shared input styles ────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', height: '44px', borderRadius: '10px',
    border: '1.5px solid #E2E8F0', padding: '0 14px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    background: '#FAFAFA', fontFamily: 'Inter, sans-serif',
  }

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#374151', marginBottom: '6px',
  }

  const selectStyle = {
    height: '40px', borderRadius: '10px', padding: '0 14px',
    border: '1.5px solid #E2E8F0', fontSize: '13px',
    outline: 'none', background: 'white', cursor: 'pointer',
    fontFamily: 'Inter, sans-serif', color: '#374151',
  }

  const btnStyle = {
    padding: '5px 0', borderRadius: '6px', fontSize: '11px',
    fontWeight: '600', cursor: 'pointer', width: '56px', textAlign: 'center',
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', minHeight: '100vh',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      background: '#F7F8FC',
    }}>
      <Sidebar session={session} />
      

      <div style={{ marginLeft: '240px', flex: 1, padding: '32px', minWidth: 0 }}>

        {/* ── Page Header ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: '20px',
        }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '800', color: '#0F172A', margin: '0 0 4px', letterSpacing: '-0.4px' }}>
              Invoices
            </h1>
            <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
              {filteredInvoices.length} of {invoices.length} invoices
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Convert quote button — only shown if there are accepted quotes */}
            {quotes.length > 0 && (
              <button onClick={() => setShowConvertModal(true)} style={{
                height: '40px', padding: '0 20px', borderRadius: '10px',
                background: 'white', border: '1.5px solid #E2E8F0',
                color: '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}>Convert Quote →</button>
            )}
            <button onClick={() => { setConvertingQuote(null); resetForm(); setShowAddModal(true) }} style={{
              height: '40px', padding: '0 20px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
              color: 'white', fontSize: '13px', fontWeight: '700',
              border: 'none', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(37,99,235,0.30)',
            }}>+ New Invoice</button>
          </div>
        </div>

        <TrialBanner session={session} />

        {/* ── Filter bar ── */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search by customer or invoice number..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, height: '40px', borderRadius: '10px',
              border: '1.5px solid #E2E8F0', padding: '0 14px',
              fontSize: '13px', outline: 'none', background: 'white',
              fontFamily: 'Inter, sans-serif',
            }}
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ ...selectStyle, minWidth: '140px' }}>
            <option value="All">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ ...selectStyle, minWidth: '150px' }}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Value</option>
            <option value="lowest">Lowest Value</option>
          </select>
        </div>

        {/* ── Invoices Table ── */}
        <div style={{
          background: 'white', borderRadius: '12px',
          border: '1px solid #EAECF0',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          width: '100%', overflowX: 'auto',
        }}>
          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '110px 1fr 100px 100px 110px 110px 240px',
            padding: '10px 20px', background: '#F8FAFC',
            borderBottom: '1px solid #EAECF0', minWidth: '900px',
          }}>
            {['Invoice #', 'Customer', 'Total', 'Status', 'Issued', 'Due', 'Actions'].map((h, i) => (
              <span key={i} style={{
                fontSize: '11px', fontWeight: '600', color: '#94A3B8',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>{h}</span>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94A3B8' }}>Loading...</div>
          ) : filteredInvoices.length === 0 ? (
            <div style={{ padding: '64px', textAlign: 'center' }}>
              <p style={{ fontSize: '15px', fontWeight: '700', color: '#0F172A', margin: '0 0 6px' }}>
                {invoices.length === 0 ? 'No invoices yet' : 'No invoices match your filters'}
              </p>
              <p style={{ fontSize: '13px', color: '#94A3B8', margin: '0 0 20px' }}>
                {invoices.length === 0
                  ? quotes.length > 0 ? 'Convert an accepted quote or create a new invoice' : 'Create your first invoice'
                  : 'Try changing your search or filters'}
              </p>
              {invoices.length === 0 && (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  {quotes.length > 0 && (
                    <button onClick={() => setShowConvertModal(true)} style={{
                      padding: '8px 20px', borderRadius: '8px',
                      background: 'white', border: '1.5px solid #E2E8F0',
                      color: '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                    }}>Convert Quote →</button>
                  )}
                  <button onClick={() => { resetForm(); setShowAddModal(true) }} style={{
                    padding: '8px 20px', borderRadius: '8px',
                    background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                    color: 'white', fontSize: '13px', fontWeight: '600',
                    border: 'none', cursor: 'pointer',
                  }}>+ New Invoice</button>
                </div>
              )}
            </div>
          ) : (
            filteredInvoices.map((invoice, i) => (
              <div key={invoice.id} style={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr 100px 100px 110px 110px 240px',
                padding: '14px 20px', alignItems: 'center',
                borderBottom: i < filteredInvoices.length - 1 ? '1px solid #F1F5F9' : 'none',
                minWidth: '900px',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFBFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Invoice number */}
                <div>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#2563EB' }}>
                    INV-{String(invoice.invoice_number_numeric).padStart(3, '0')}
                  </span>
                  {invoice.quote_id && (
                    <p style={{ fontSize: '10px', color: '#94A3B8', margin: '2px 0 0', fontWeight: '500' }}>
                      From quote
                    </p>
                  )}
                </div>

                {/* Customer */}
                <div>
                  <p style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A', margin: '0 0 2px' }}>
                    {invoice.customers?.customer_name || '—'}
                  </p>
                  <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                    {invoice.customers?.customer_type || ''}
                  </span>
                </div>

                {/* Total */}
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>
                  £{parseFloat(invoice.total || 0).toFixed(2)}
                </span>

                {/* Status */}
                <div><span style={statusStyle(invoice.status)}>{invoice.status}</span></div>

                {/* Issue date */}
                <span style={{ fontSize: '12px', color: '#94A3B8' }}>
                  {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString('en-GB') : '—'}
                </span>

                {/* Due date */}
                <span style={{
                  fontSize: '12px',
                  color: invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'Paid' ? '#BE123C' : '#94A3B8',
                  fontWeight: invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'Paid' ? '600' : '400',
                }}>
                  {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-GB') : '—'}
                </span>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button onClick={() => navigate(`/invoices/${invoice.id}`)} style={{
                    ...btnStyle, border: '1.5px solid #E2E8F0',
                    background: 'white', color: '#374151',
                  }}>View</button>
                  {invoice.status === 'Draft' && (
                    <button onClick={() => handleStatusChange(invoice.id, 'Sent')} style={{
                      ...btnStyle, border: 'none',
                      background: 'linear-gradient(135deg, #2563EB, #1D4ED8)', color: 'white',
                    }}>Send</button>
                  )}
                  {invoice.status === 'Sent' && (
                    <button onClick={() => handleStatusChange(invoice.id, 'Paid')} style={{
                      ...btnStyle, border: 'none', background: '#15803D', color: 'white',
                    }}>Paid</button>
                  )}
                  {(invoice.status === 'Sent' || invoice.status === 'Overdue') && (
                    <button onClick={() => handleStatusChange(invoice.id, 'Overdue')} style={{
                      ...btnStyle, border: '1.5px solid #FECDD3', background: 'white', color: '#BE123C',
                      width: '64px',
                    }}>Overdue</button>
                  )}
                  <button onClick={() => handleDelete(invoice.id)} style={{
                    ...btnStyle, border: '1.5px solid #E2E8F0', background: 'white', color: '#94A3B8',
                  }}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Convert Quote Modal ──────────────────────────────────────────────────
          Shows a list of accepted quotes that haven't been converted yet.
          Selecting one pre-fills the invoice form with quote data.
      ─────────────────────────────────────────────────────────────────────── */}
      {showConvertModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '24px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '520px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '80vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 }}>
                Convert Quote to Invoice
              </h2>
              <div onClick={() => setShowConvertModal(false)} style={{
                width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '16px', color: '#64748B',
              }}>×</div>
            </div>

            <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 20px' }}>
              Select an accepted quote to convert. Line items and details will be pre-filled.
            </p>

            {quotes.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#94A3B8', textAlign: 'center', padding: '24px' }}>
                No accepted quotes available to convert.
              </p>
            ) : (
              quotes.map((quote, i) => (
                <div key={quote.id}
                  onClick={() => handleSelectQuoteToConvert(quote)}
                  style={{
                    padding: '14px 16px', borderRadius: '10px',
                    border: '1.5px solid #E2E8F0', marginBottom: '8px',
                    cursor: 'pointer', transition: 'all 0.15s',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.border = '1.5px solid #2563EB'; e.currentTarget.style.background = '#EFF6FF' }}
                  onMouseLeave={e => { e.currentTarget.style.border = '1.5px solid #E2E8F0'; e.currentTarget.style.background = 'transparent' }}
                >
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: '700', color: '#2563EB', margin: '0 0 2px' }}>
                      Q-{String(quote.quote_number_numeric).padStart(3, '0')}
                    </p>
                    <p style={{ fontSize: '13px', color: '#0F172A', margin: '0 0 2px', fontWeight: '600' }}>
                      {quote.customers?.customer_name}
                    </p>
                    <p style={{ fontSize: '12px', color: '#94A3B8', margin: 0 }}>
                      {new Date(quote.created_at).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '14px', fontWeight: '800', color: '#0F172A', margin: '0 0 4px' }}>
                      £{parseFloat(quote.total || 0).toFixed(2)}
                    </p>
                    <span style={{
                      fontSize: '11px', fontWeight: '600', padding: '2px 8px',
                      borderRadius: '999px', background: '#F0FDF4', color: '#15803D',
                    }}>{quote.status}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── New Invoice Modal ────────────────────────────────────────────────────
          Used for both standalone invoices and converted quotes.
          When converting, form is pre-filled with quote data.
      ─────────────────────────────────────────────────────────────────────── */}
      {showAddModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '24px',
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '700px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: '0 0 2px' }}>
                  {convertingQuote ? 'Convert to Invoice' : 'New Invoice'}
                </h2>
                {convertingQuote && (
                  <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>
                    Converting Q-{String(convertingQuote.quote_number_numeric).padStart(3, '0')} — edit details below
                  </p>
                )}
              </div>
              <div onClick={() => { setShowAddModal(false); setConvertingQuote(null); resetForm() }} style={{
                width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '16px', color: '#64748B',
              }}>×</div>
            </div>

            {error && (
              <div style={{
                background: '#FFF1F2', border: '1px solid #FECDD3',
                borderRadius: '8px', padding: '12px',
                color: '#BE123C', fontSize: '13px', marginBottom: '16px',
              }}>{error}</div>
            )}

            {customers.length === 0 ? (
              <div style={{
                background: '#FFF8E1', border: '1px solid #FDE68A',
                borderRadius: '8px', padding: '16px',
                color: '#92400E', fontSize: '13px', textAlign: 'center',
              }}>You need to add a customer first before creating an invoice.</div>
            ) : (
              <>
                {/* Customer + Reference */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={labelStyle}>Customer *</label>
                    <select value={form.customer_id}
                      onChange={e => setForm({ ...form, customer_id: e.target.value })}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                      <option value="">Select customer...</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.customer_name} ({c.customer_type || 'Business'})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Reference / PO Number</label>
                    <input type="text" value={form.reference}
                      onChange={e => setForm({ ...form, reference: e.target.value })}
                      placeholder="e.g. PO-12345" style={inputStyle} />
                  </div>
                </div>

                {/* Issue date + Due date */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={labelStyle}>Issue Date</label>
                    <input type="date" value={form.issue_date}
                      onChange={e => setForm({ ...form, issue_date: e.target.value })}
                      style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Due Date</label>
                    <input type="date" value={form.due_date}
                      onChange={e => setForm({ ...form, due_date: e.target.value })}
                      style={inputStyle} />
                  </div>
                </div>

                {/* Line items */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Line Items</label>
                    <button onClick={addItem} style={{
                      padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
                      fontWeight: '600', border: '1.5px solid #2563EB',
                      background: 'white', color: '#2563EB', cursor: 'pointer',
                    }}>+ Add Item</button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 32px', gap: '8px', marginBottom: '6px', padding: '0 4px' }}>
                    {['Description', 'Qty', 'Unit Price', 'Total', ''].map((h, i) => (
                      <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</span>
                    ))}
                  </div>

                  {form.items.map((item, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 32px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <input type="text" value={item.description}
                        onChange={e => updateItem(index, 'description', e.target.value)}
                        placeholder="e.g. Labour" style={{ ...inputStyle, height: '40px' }} />
                      <input type="number" value={item.quantity}
                        onChange={e => updateItem(index, 'quantity', e.target.value)}
                        min="0" style={{ ...inputStyle, height: '40px' }} />
                      <input type="number" value={item.unit_price}
                        onChange={e => updateItem(index, 'unit_price', e.target.value)}
                        min="0" placeholder="0.00" style={{ ...inputStyle, height: '40px' }} />
                      <div style={{
                        height: '40px', borderRadius: '10px', background: '#F8FAFC',
                        border: '1.5px solid #E2E8F0', display: 'flex', alignItems: 'center',
                        padding: '0 14px', fontSize: '14px', fontWeight: '600', color: '#0F172A',
                      }}>£{parseFloat(item.total || 0).toFixed(2)}</div>
                      <button onClick={() => removeItem(index)} disabled={form.items.length === 1} style={{
                        width: '32px', height: '32px', borderRadius: '6px',
                        border: '1.5px solid #FECDD3', background: 'white',
                        color: '#BE123C', cursor: form.items.length === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: form.items.length === 1 ? 0.3 : 1,
                      }}>×</button>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div style={{ background: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ width: '280px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', color: '#64748B' }}>Subtotal</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>£{subtotal.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div onClick={() => setForm({ ...form, vat_enabled: !form.vat_enabled })} style={{
                            width: '18px', height: '18px', borderRadius: '4px',
                            border: `2px solid ${form.vat_enabled ? '#2563EB' : '#E2E8F0'}`,
                            background: form.vat_enabled ? '#2563EB' : 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                          }}>
                            {form.vat_enabled && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            )}
                          </div>
                          <span style={{ fontSize: '13px', color: '#64748B' }}>VAT</span>
                          {form.vat_enabled && (
                            <>
                              <input type="number" value={form.vat_rate}
                                onChange={e => setForm({ ...form, vat_rate: e.target.value })}
                                style={{ width: '50px', height: '28px', borderRadius: '6px', border: '1.5px solid #E2E8F0', padding: '0 8px', fontSize: '13px', outline: 'none', background: 'white' }} />
                              <span style={{ fontSize: '13px', color: '#64748B' }}>%</span>
                            </>
                          )}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>£{vatAmount.toFixed(2)}</span>
                      </div>
                      <div style={{ height: '1px', background: '#E2E8F0', margin: '8px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>Total</span>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#2563EB' }}>£{grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes + Payment Terms + T&Cs */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    style={{ width: '100%', height: '80px', borderRadius: '10px', border: '1.5px solid #E2E8F0', padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'Inter, sans-serif' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Payment Terms</label>
                  <input type="text" value={form.payment_terms}
                    onChange={e => setForm({ ...form, payment_terms: e.target.value })}
                    placeholder="e.g. Payment due within 30 days" style={inputStyle} />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Terms & Conditions</label>
                  <textarea value={form.terms_conditions} onChange={e => setForm({ ...form, terms_conditions: e.target.value })}
                    placeholder="Your terms and conditions..."
                    style={{ width: '100%', height: '80px', borderRadius: '10px', border: '1.5px solid #E2E8F0', padding: '12px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA', resize: 'vertical', fontFamily: 'Inter, sans-serif' }} />
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => { setShowAddModal(false); setConvertingQuote(null); resetForm() }} style={{
                    flex: 1, height: '44px', borderRadius: '10px',
                    background: 'white', border: '1.5px solid #E2E8F0',
                    fontSize: '14px', fontWeight: '600', color: '#64748B', cursor: 'pointer',
                  }}>Cancel</button>
                  <button onClick={handleCreate} disabled={saving} style={{
                    flex: 1, height: '44px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                    color: 'white', fontSize: '14px', fontWeight: '700',
                    border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                  }}>{saving ? 'Creating...' : convertingQuote ? 'Create Invoice from Quote' : 'Create Invoice'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/*
──────────────────────────────────────────────────────────────────────────────
SQL — Run in Supabase SQL Editor to create required tables
──────────────────────────────────────────────────────────────────────────────

-- Invoices table
CREATE TABLE IF NOT EXISTS invoices (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  company_id uuid REFERENCES companies(id) ON DELETE CASCADE,
  customer_id uuid REFERENCES customers(id) ON DELETE SET NULL,
  quote_id uuid REFERENCES quotes(id) ON DELETE SET NULL,
  invoice_number_numeric int NOT NULL DEFAULT 1,
  status text NOT NULL DEFAULT 'Draft',
  reference text,
  issue_date date,
  due_date date,
  notes text,
  payment_terms text,
  terms_conditions text,
  vat_enabled boolean DEFAULT true,
  vat_rate numeric DEFAULT 20,
  subtotal numeric DEFAULT 0,
  vat_amount numeric DEFAULT 0,
  total numeric DEFAULT 0,
  created_at timestamptz DEFAULT now()
);

-- Invoice items table
CREATE TABLE IF NOT EXISTS invoice_items (
  id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  invoice_id uuid REFERENCES invoices(id) ON DELETE CASCADE,
  description text NOT NULL,
  quantity numeric NOT NULL DEFAULT 1,
  unit_price numeric NOT NULL DEFAULT 0,
  total numeric NOT NULL DEFAULT 0,
  created_at timestamptz DEFAULT now()
);

-- Add invoice number counter to companies
ALTER TABLE companies ADD COLUMN IF NOT EXISTS next_invoice_number int DEFAULT 1;

-- RLS policies
ALTER TABLE invoices ENABLE ROW LEVEL SECURITY;
ALTER TABLE invoice_items ENABLE ROW LEVEL SECURITY;

CREATE POLICY "Users can manage own invoices"
ON invoices FOR ALL
USING (company_id IN (
  SELECT company_id FROM profiles WHERE id = auth.uid()
));

CREATE POLICY "Users can manage own invoice items"
ON invoice_items FOR ALL
USING (invoice_id IN (
  SELECT id FROM invoices WHERE company_id IN (
    SELECT company_id FROM profiles WHERE id = auth.uid()
  )
));

──────────────────────────────────────────────────────────────────────────────
*/